// Окно создания и правки (components/Modal/README.md) на Radix Dialog: Tab ходит по кругу внутри
// окна, Esc и клик по затемнению закрывают верхний слой, фокус возвращается на элемент, который
// открыл окно. До 640 px окно выезжает снизу.
// 044: окно уходит с выходом (t-modal-out «Табеля», затемнение — kp-fade-out); фокус на опору — в
// начале выхода; Esc, нажатый, пока окно уходит, закрывает следующий слой (MOT-06, MOT-07).
// 045 (OPN-13): окно выше экрана прокручивает только тело (kp.css); тело без полей и кнопок, которое
// прокручивается, встаёт в обход Tab — его прокручивают клавишами (axe scrollable-region-focusable).
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { cx } from '../lib/cx.ts';
import { keepOpenForToasts, relayEscape } from '../lib/layers.ts';
import { modKey } from '../lib/platform.ts';
import { useReturnFocus } from '../lib/return-focus.ts';
import { ToastLayerRegion } from '../lib/toast-layer.tsx';
import { Button } from './Button.tsx';
import { IconButton } from './IconButton.tsx';
import { Kbd } from './Kbd.tsx';

export type ModalSize = 'sm' | 'md' | 'lg';

const sizeClass: Record<ModalSize, string | undefined> = {
  sm: 'kp-modal-sm',
  md: undefined,
  lg: 'kp-modal-lg',
};

/**
 * Куда встаёт фокус при открытии: отмеченное data-autofocus (в теле или подвале), иначе первое
 * поле ввода, иначе первая кнопка тела окна, иначе само окно. «Закрыть» в шапке фокус при
 * открытии не получает (044, MOT-15): иначе над подзаголовком всплывала «Закрыть Esc».
 */
const AUTOFOCUS = '[data-autofocus]:not([disabled])';
const FIRST_FIELD =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])';
const FIRST_FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Тело окна прокручивается, а полей и кнопок в нём нет (045, OPN-13; ревью: T043) — оно встаёт
 * в обход Tab (tabIndex 0): клавиши прокрутки работают, только когда фокус в прокручиваемой части.
 * Высота тела и его содержимого — ResizeObserver, смена содержимого — MutationObserver.
 */
function useKeyboardScroll(body: HTMLDivElement | null): boolean {
  const [reachable, setReachable] = useState(false);
  useEffect(() => {
    if (!body || typeof ResizeObserver !== 'function') return undefined;
    const check = () => {
      const scrolls = body.scrollHeight > body.clientHeight + 1;
      setReachable(scrolls && body.querySelector(`${FIRST_FIELD}, ${FIRST_FOCUSABLE}`) === null);
    };
    const resize = new ResizeObserver(check);
    const watch = () => {
      resize.disconnect();
      resize.observe(body);
      for (const child of Array.from(body.children)) resize.observe(child);
    };
    const mutation = new MutationObserver(() => {
      watch();
      check();
    });
    watch();
    mutation.observe(body, { childList: true, subtree: true });
    check();
    return () => {
      resize.disconnect();
      mutation.disconnect();
    };
  }, [body]);
  return body !== null && reachable;
}

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Заголовок dialog-title. */
  title: ReactNode;
  /** Одна фраза о назначении окна. */
  description?: ReactNode;
  /** 420 px — подтверждение и одно поле, 520 px — форма, 720 px — справка. */
  size?: ModalSize;
  /** Подвал: ModalFooter. */
  footer?: ReactNode;
  /** ⌘ ↵ на Mac, Ctrl ↵ на остальных — отправка длинной формы; слушаются обе клавиши. */
  onSubmitShortcut?: () => void;
  /** Вложенный редактор может обработать Escape, не закрывая всю форму. */
  onEscapeKeyDown?: (event: globalThis.KeyboardEvent) => void;
  /** Куда вернуть фокус после закрытия, если окно открыли из меню; иначе — откуда открыли. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  children?: ReactNode;
  className?: string;
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  footer,
  onSubmitShortcut,
  onEscapeKeyDown,
  returnFocusRef,
  children,
  className,
}: ModalProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocus = useReturnFocus(open, contentRef, { returnFocusRef });
  // Тело — в портале Radix: узел появляется после монтирования портала, поэтому ref-функция.
  const [bodyNode, setBodyNode] = useState<HTMLDivElement | null>(null);
  const attachBody = useCallback((node: HTMLDivElement | null) => {
    bodyRef.current = node;
    setBodyNode(node);
  }, []);
  const scrollable = useKeyboardScroll(bodyNode);

  const focusFirst = (event: Event) => {
    const body = bodyRef.current;
    const target =
      contentRef.current?.querySelector<HTMLElement>(AUTOFOCUS) ??
      body?.querySelector<HTMLElement>(FIRST_FIELD) ??
      body?.querySelector<HTMLElement>(FIRST_FOCUSABLE);
    event.preventDefault();
    // Окно без полей и кнопок тела — фокус на само окно (tabIndex −1 у FocusScope Radix).
    (target ?? contentRef.current)?.focus();
  };

  const onEscape = (event: globalThis.KeyboardEvent) => {
    if (!open) {
      event.preventDefault();
      relayEscape(contentRef.current, event);
      return;
    }
    onEscapeKeyDown?.(event);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (onSubmitShortcut && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSubmitShortcut();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="kp-overlay">
          <Dialog.Content
            ref={contentRef}
            className={cx('t-modal', sizeClass[size], className)}
            onOpenAutoFocus={focusFirst}
            onCloseAutoFocus={returnFocus}
            onKeyDown={onKeyDown}
            onEscapeKeyDown={onEscape}
            onPointerDownOutside={keepOpenForToasts}
            onInteractOutside={keepOpenForToasts}
            {...(description == null ? { 'aria-describedby': undefined } : {})}
          >
            <div className="t-modal-h">
              <div className="kp-grow">
                <Dialog.Title asChild>
                  <h2>{title}</h2>
                </Dialog.Title>
                {description != null ? (
                  <Dialog.Description asChild>
                    <p>{description}</p>
                  </Dialog.Description>
                ) : null}
              </div>
              <Dialog.Close asChild>
                <IconButton icon={X} label="Закрыть" shortcut="Esc" size="sm" />
              </Dialog.Close>
            </div>
            <div ref={attachBody} className="t-modal-b" tabIndex={scrollable ? 0 : undefined}>
              {children}
            </div>
            {footer}
            <ToastLayerRegion active={open} />
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ModalFooterProps {
  /** Слева: «Создать ещё», «Скопировать ссылку». */
  secondary?: ReactNode;
  /** Подсказка клавиши отправки перед кнопками: ⌘ ↵ на Mac, Ctrl ↵ на остальных (FR-037). */
  submitHint?: boolean;
  /** «Отмена»; null — кнопки нет (всё применяется сразу). */
  cancelLabel?: string | null;
  onCancel?: () => void;
  /** Главная кнопка справа. */
  primary?: ReactNode;
}

/** Подвал окна: вторичное слева → распорка → «Отмена» → главное справа. */
export function ModalFooter({
  secondary,
  submitHint = false,
  cancelLabel = 'Отмена',
  onCancel,
  primary,
}: ModalFooterProps) {
  return (
    <div className="t-modal-f">
      {secondary}
      <span className="sp" />
      {submitHint ? (
        <span className="kp-modal-hint kp-hide-phone" aria-hidden="true">
          <Kbd>{modKey()}</Kbd>
          <Kbd>↵</Kbd>
        </span>
      ) : null}
      {cancelLabel !== null ? (
        onCancel ? (
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
        ) : (
          <Dialog.Close asChild>
            <Button variant="secondary">{cancelLabel}</Button>
          </Dialog.Close>
        )
      ) : null}
      {primary}
    </div>
  );
}
